import { useEffect, useState } from "react";
import { MenuItem, Select, Tooltip } from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";

// Languages offered by the Google Website Translator widget.
// The codes must match `includedLanguages` in public/index.html.
export const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "ar", label: "العربية (Arabic)" },
  { code: "ur", label: "اردو (Urdu)" },
  { code: "it", label: "Italiano (Italian)" },
  { code: "fr", label: "Français (French)" },
];

const COOKIE_NAME = "googtrans";

const readCurrentLanguage = () => {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]+)`)
  );
  if (!match) return "en";
  const parts = decodeURIComponent(match[1]).split("/");
  return parts[2] || "en";
};

const writeLanguageCookie = (lang) => {
  const value = `/en/${lang}`;
  // Clear any cookie previously set on a different path/domain first.
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  document.cookie = `${COOKIE_NAME}=${value}; path=/`;
  document.cookie = `${COOKIE_NAME}=${value}; path=/; domain=${window.location.hostname}`;
};

// Drive Google's hidden <select> directly so the page is translated without
// a full reload. Returns false if the widget has not loaded yet.
const applyToGoogleWidget = (lang) => {
  const combo = document.querySelector(".goog-te-combo");
  if (!combo) return false;
  combo.value = lang;
  combo.dispatchEvent(new Event("change", { bubbles: true }));
  return true;
};

const LanguageSelector = () => {
  const [language, setLanguage] = useState("en");

  useEffect(() => {
    setLanguage(readCurrentLanguage());
  }, []);

  // Google injects a banner/tooltip into <body> when a page is translated.
  // The newer UI uses obfuscated VIpgJd-* classes and wraps the banner in a
  // body-level .skiptranslate div, so target those too.
  useEffect(() => {
    const GOOGLE_CHROME =
      ".goog-te-banner-frame, .goog-te-balloon-frame, #goog-gt-tt, " +
      ".VIpgJd-ZVi9od-ORHb-OEVmcd, .VIpgJd-ZVi9od-l4eHX-hSRGPd, " +
      ".VIpgJd-yAWNEb-L7lbkb";

    const stripGoogleChrome = (root = document) => {
      root.querySelectorAll(GOOGLE_CHROME).forEach((node) => {
        const wrapper = node.closest?.(".skiptranslate");
        if (wrapper && wrapper.parentElement === document.body) {
          wrapper.remove();
        } else {
          node.remove();
        }
      });
      document.body.style.top = "0";
    };

    stripGoogleChrome();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches?.(GOOGLE_CHROME)) {
            stripGoogleChrome(node.parentElement || document);
          } else {
            stripGoogleChrome(node);
          }
        });
      });
      document.body.style.top = "0";
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const handleChange = (event) => {
    const lang = event.target.value;
    setLanguage(lang);
    writeLanguageCookie(lang);

    // Fall back to a reload when the translator widget is not ready yet
    // (the cookie is read by the widget on load).
    if (!applyToGoogleWidget(lang)) {
      window.location.reload();
    }
  };

  return (
    <Tooltip title="Translate page">
      <Select
        className="notranslate"
        size="small"
        value={language}
        onChange={handleChange}
        startAdornment={
          <TranslateIcon sx={{ fontSize: 20, mr: 1, color: "text.secondary" }} />
        }
        sx={{
          minWidth: 130,
          mr: 1,
          "& .MuiSelect-select": { py: 0.75, display: "flex", alignItems: "center" },
        }}
        MenuProps={{ disablePortal: true }}
      >
        {LANGUAGES.map((item) => (
          <MenuItem key={item.code} value={item.code} className="notranslate">
            {item.label}
          </MenuItem>
        ))}
      </Select>
    </Tooltip>
  );
};

export default LanguageSelector;
